import { Component, ElementRef, Input, ViewChild } from '@angular/core';
import { AlertModule } from '@rogers/cdk/alert';
import { FormsModule } from '@angular/forms';
import { IconButtonComponent } from '../icon-button/icon-button.component';
import { HighlightDirective } from '../utils/highlight.directive';
import { CommonModule } from '@angular/common';
import { IconModule } from '@rogers/cdk/icon';
import { AutocompleteSuggestionsComponent } from '../autocomplete-suggestions/autocomplete-suggestions.component';

@Component({
  standalone: true,
  imports: [
    CommonModule,
    AlertModule,
    FormsModule,
    IconModule,
    IconButtonComponent,
    HighlightDirective,
    AutocompleteSuggestionsComponent,
  ],
  selector: 'ucx-footer-message',
  templateUrl: './footer-message.component.html',
  styleUrls: ['./footer-message.component.scss'],
})
export class FooterMessageComponent {
  @Input() maxNoCharacters = 250;
  @Input() inputPlaceHolder = 'Type message here';
  @Input() closeLabel = 'Close alert';
  @Input() alertHeading = 'Message too long';
  @Input() suggestionsList;
  @Input() isAutoComplete = false;
  @Input() alertText =
    'For better accuracy, consider asking one question at a time.';
  @Input() sendMessageIcon =
    'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyB3aWR0aD0iMjRweCIgaGVpZ2h0PSIyNHB4IiB2aWV3Qm94PSIwIDAgMjQgMjQiIHZlcnNpb249IjEuMSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB4bWxuczp4bGluaz0iaHR0cDovL3d3dy53My5vcmcvMTk5OS94bGluayI+CiAgICA8dGl0bGU+NTA1MUREREUtNkY5NC00REExLTg5QjQtRTMxNzM4QzA0NjRGQDF4PC90aXRsZT4KICAgIDxnIGlkPSJTeW1ib2xzIiBzdHJva2U9Im5vbmUiIHN0cm9rZS13aWR0aD0iMSIgZmlsbD0ibm9uZSIgZmlsbC1ydWxlPSJldmVub2RkIj4KICAgICAgICA8ZyBpZD0iTmF2aWdhdGlvbi0vLVJEUy0vLTI0LS8tQXJyb3ctLy1VcCIgZmlsbC1ydWxlPSJub256ZXJvIj4KICAgICAgICAgICAgPHJlY3QgaWQ9IkJvdW5kaW5nLUJveCIgeD0iMCIgeT0iMCIgd2lkdGg9IjI0IiBoZWlnaHQ9IjI0Ij48L3JlY3Q+CiAgICAgICAgICAgIDxwYXRoIGQ9Ik02LjU4NTc4NjQ0LDExIEwxOS45NjEyOTg4LDExIEwxOS45NjEyOTg4LDEzIEw2LjU4NTc4NjQ0LDEzIEw5LjgyODQyNzEyLDE2LjI0MjY0MDcgQzEwLjc3MTIzNjIsMTcuMTg1NDQ5NyAxMC43NzEyMzYyLDE4LjEyODI1ODggOS44Mjg0MjcxMiwxOS4wNzEwNjc4IEw0Ljg3ODY3OTY2LDE0LjEyMTMyMDMgQzMuNzA3MTA2NzgsMTIuOTQ5NzQ3NSAzLjcwNzEwNjc4LDExLjA1MDI1MjUgNC44Nzg2Nzk2Niw5Ljg3ODY3OTY2IEw5LjgyODQyNzEyLDQuOTI4OTMyMTkgQzEwLjc3MTIzNjIsNS44NzE3NDEyMyAxMC43NzEyMzYyLDYuODE0NTUwMjcgOS44Mjg0MjcxMiw3Ljc1NzM1OTMxIEw2LjU4NTc4NjQ0LDExIFoiIGlkPSLwn46oLUljb24tQ29sb3VyIiBmaWxsPSJ3aGl0ZSIgdHJhbnNmb3JtPSJ0cmFuc2xhdGUoMTEuOTgwNjQ5LCAxMi4wMDAwMDApIHJvdGF0ZSg5MC4wMDAwMDApIHRyYW5zbGF0ZSgtMTEuOTgwNjQ5LCAtMTIuMDAwMDAwKSAiPjwvcGF0aD4KICAgICAgICA8L2c+CiAgICA8L2c+Cjwvc3ZnPg==';
  @Input() sendMessageAltText = 'Send Message';
  @Input()
  set isDisabled(value: boolean | null) {
    this._isDisabled = !!value;
    if (!value) {
      this.setFocusBack();
    }
  }
  get isDisabled(): boolean {
    return this._isDisabled;
  }

  @Input() onSendMessage;
  @Input() onUserTyping;
  @Input() onSearch;
  @Input() onClearSuggestions;

  @ViewChild('textAreaElement') textAreaElement: ElementRef | undefined;

  originalParaHeight = 24;
  textareaText = '';
  _isDisabled = false;
  remainingCharacterCountThreshold = 50;
  _currentSearchEntry = '';
  suggestionsOffset = 30;

  instantiateOriginalHeight() {
    if (this.textareaText.length == 0) {
      this.originalParaHeight =
        this.textAreaElement?.nativeElement.clientHeight;
    }
  }

  /*
   * A getter to determine if there are < 50 characters allowed to be
   * typed in the textarea
   * @return {boolean}
   * */
  get isCharCountVisible(): boolean {
    return (
      this.characterCount >
      this.maxNoCharacters - this.remainingCharacterCountThreshold
    );
  }

  /*
   * To get the number of input text characters
   * @return {number}
   * */
  get characterCount(): number {
    return this.textareaText?.length;
  }

  isError(): boolean {
    return this.characterCount > this.maxNoCharacters;
  }

  getRemainingCharCount(): number {
    return this.maxNoCharacters - this.characterCount;
  }

  /*
   * Emits the user input text
   * */
  sendInputMessage(): void {
    if (!this.isError()) {
      // Remove any new line
      this.textareaText = this.textareaText?.replace(/(\r\n|\n|\r)/gm, '');
      if (this.textareaText?.length) {
        this.onUserTyping(false);
        this.onSendMessage(this.textareaText);
      }
      this.clearInputMessage();
      this.setFocusBack();
    }
  }

  /*
   * Sets the focus back to the textarea
   * */
  setFocusBack(): void {
    setTimeout(() => {
      if (this.textAreaElement) {
        this.textAreaElement.nativeElement.focus();
      }
    }, 0);
  }

  /*
   * Emits the user typing action
   * */
  userTyping(): void {
    if (this.textareaText.trim()) {
      this.onUserTyping(true);
    } else {
      this.onUserTyping(false);
    }
    if (this.isAutoComplete && this._currentSearchEntry != this.textareaText) {
      this._currentSearchEntry = this.textareaText;
      this.onSearch(this.textareaText);
    }
  }

  clearInputMessage(): void {
    this.textareaText = '';
    this.adjustTextArea();
  }

  /*
   * Disable New Line in Textarea when Pressed ENTER
   */
  preventNewLine(e): void {
    if (e.keyCode == 13) {
      // prevent default behavior
      e.preventDefault();
    }
  }

  adjustTextArea() {
    const element = this.textAreaElement.nativeElement;
    element.style.height = 'auto';
    if (this.textareaText) {
      element.style.height = '1px';
      element.style.height = element.scrollHeight + 'px';
      this.suggestionsOffset = element.scrollHeight + 30;
    }
  }

  selectSuggestion(value: string): void {
    this.textAreaElement.nativeElement.value = value;
    this.textareaText = value;
    this.adjustTextArea();
    this.onClearSuggestions();
  }
}
